import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Stack,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatCalendarDate, formatDateInViewerTZ, formatDateTimeInViewerTZ, getDateAndTimeInTimezone, getViewerTimezone } from '../../utils/dateTimeUtils';
import { DemoCountryDateLines, DemoCountryTimeLines } from '../../components/Demos/DemoCountrySchedule';
import { leadsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  formatChildAgeLabel,
  formatLeadTimePreferences,
  formatListDisplay,
} from './leadDisplayUtils';
import {
  formatFullName,
  formatParentContactLabel,
  formatStoredPersonName,
} from '../../utils/personName';

function overviewVal(v) {
  if (v == null || v === '') return '—';
  const s = String(v).trim();
  return s || '—';
}

function OverviewField({ label, value, xs = 6, sm = 3 }) {
  return (
    <Grid item xs={xs} sm={sm}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {overviewVal(value)}
      </Typography>
    </Grid>
  );
}

function OverviewBand({ title, children }) {
  return (
    <Box sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', display: 'block', mb: 1 }}
      >
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        {children}
      </Grid>
    </Box>
  );
}

const LeadDetails = ({ open, onClose, lead }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const viewerTimezone = getViewerTimezone(user?.timezone || lead?.timezone);
  const [tabValue, setTabValue] = useState(0);
  const [leadDetails, setLeadDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeline, setTimeline] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [showDeletedNotes, setShowDeletedNotes] = useState(false);
  // Separate state for creation date update dialog (can be removed independently)
  const [openCreationDateDialog, setOpenCreationDateDialog] = useState(false);
  const [creationDate, setCreationDate] = useState('');
  const [updatingCreationDate, setUpdatingCreationDate] = useState(false);

  const statusLabels = {
    new: 'New',
    wip_before_demo: 'WIP (before demo)',
    demo_scheduled: 'Demo scheduled',
    demo_reschedule_requested: 'Demo reschedule requested',
    demo_completed: 'Demo Completed',
    demo_not_present: 'Demo Not Present',
    wip_after_demo: 'WIP (after demo)',
    deferred: 'Deferred',
    enrollment_pending: 'Enrollment Pending',
    won: 'Won',
    lost: 'Lost',
    dropped: 'Dropped'
  };

  const statusColors = {
    new: 'primary',
    wip_before_demo: 'info',
    demo_scheduled: 'info',
    demo_reschedule_requested: 'warning',
    demo_completed: 'success',
    demo_not_present: 'warning',
    wip_after_demo: 'warning',
    deferred: 'default',
    enrollment_pending: 'info',
    won: 'success',
    lost: 'error',
    dropped: 'error'
  };

  const demoStatusLabels = {
    scheduled: 'Scheduled',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rescheduled: 'Rescheduled'
  };

  const demoStatusColors = {
    scheduled: 'primary',
    completed: 'success',
    cancelled: 'error',
    rescheduled: 'warning'
  };

  const fetchLeadDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await leadsAPI.getById(lead.id);
      // Backend returns { lead: {...} }, so extract the lead object
      const leadData = response.data?.lead || response.data;
      setLeadDetails(leadData);
    } catch (error) {
      setError('Failed to fetch lead details');
      console.error('Error fetching lead details:', error);
    } finally {
      setLoading(false);
    }
  }, [lead?.id]);

  const fetchTimeline = useCallback(async () => {
    try {
      const res = await leadsAPI.getTimeline(lead.id);
      setTimeline(res.data?.data?.timeline || []);
      setStatusHistory(res.data?.data?.status_history || []);
    } catch (err) {
      console.error('Error fetching timeline:', err);
    }
  }, [lead?.id]);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await leadsAPI.getNotes(lead.id, showDeletedNotes ? { include_deleted: true } : undefined);
      setNotes(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
    }
  }, [lead?.id, showDeletedNotes]);

  useEffect(() => {
    if (open && lead) {
      fetchLeadDetails();
      fetchTimeline();
      fetchNotes();
    }
  }, [open, lead, fetchLeadDetails, fetchTimeline, fetchNotes]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Note text is required');
      return;
    }
    try {
      await leadsAPI.addNote(lead.id, { note_text: noteText.trim() });
      setNoteText('');
      fetchNotes();
      fetchTimeline();
      toast.success('Note added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await leadsAPI.deleteNote(lead.id, noteId);
      fetchNotes();
      fetchTimeline();
      toast.success('Note deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete note');
    }
  };

  // Separate function to handle creation date update (can be removed independently)
  const handleOpenCreationDateDialog = () => {
    if (leadDetails && leadDetails.created_at && viewerTimezone) {
      const inViewerTZ = getDateAndTimeInTimezone(leadDetails.created_at, viewerTimezone);
      setCreationDate(inViewerTZ ? inViewerTZ.dateStr : '');
    } else if (leadDetails && leadDetails.created_at) {
      const date = new Date(leadDetails.created_at);
      setCreationDate(date.toISOString().split('T')[0]);
    } else {
      setCreationDate('');
    }
    setOpenCreationDateDialog(true);
  };

  const handleCloseCreationDateDialog = () => {
    setOpenCreationDateDialog(false);
    setCreationDate('');
  };

  const handleUpdateCreationDate = async () => {
    if (!creationDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      setUpdatingCreationDate(true);
      await leadsAPI.updateCreationDate(lead.id, { created_at: creationDate });
      toast.success('Creation date updated successfully');
      handleCloseCreationDateDialog();
      fetchLeadDetails(); // Refresh lead details
    } catch (error) {
      console.error('Error updating creation date:', error);
      toast.error(error.response?.data?.error || 'Failed to update creation date');
    } finally {
      setUpdatingCreationDate(false);
    }
  };

  const formatDate = (dateString) => {
    return formatDateInViewerTZ(dateString, viewerTimezone) || (dateString ? new Date(dateString).toLocaleDateString() : '');
  };

  const formatDateTime = (dateString, timeString) => {
    if (!dateString) return '';
    const iso = timeString ? (dateString.includes('T') ? dateString : `${dateString}T${timeString}`) : dateString;
    return formatDateTimeInViewerTZ(iso, viewerTimezone) || new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return formatDateInViewerTZ(dateString, viewerTimezone);
  };

  const groupNotesByDate = (notes) => {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    notes.forEach(note => {
      const noteDate = new Date(note.created_at);
      if (noteDate >= today) {
        groups.today.push(note);
      } else if (noteDate >= yesterday) {
        groups.yesterday.push(note);
      } else if (noteDate >= weekAgo) {
        groups.thisWeek.push(note);
      } else {
        groups.older.push(note);
      }
    });

    return groups;
  };

  const combinedTimeline = useMemo(() => {
    const merged = [
      ...(timeline || []).map((t) => ({ ...t, kind: 'activity', ts: t.created_at })),
      ...(statusHistory || []).map((s) => ({ 
        ...s, 
        kind: 'status', 
        ts: s.created_at,
        old_sub_status: s.old_sub_status,
        new_sub_status: s.new_sub_status
      })),
    ];
    return merged.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  }, [timeline, statusHistory]);

  if (!lead) return null;

    const content = (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {leadDetails && (
        <>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Overview" />
            <Tab label="Demos" />
            <Tab label="Status History" />
            <Tab label="Timeline" />
            <Tab label="Notes" />
          </Tabs>

            {tabValue === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                      Status & Summary
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Box>
                        <Chip
                          label={statusLabels[leadDetails.status] || leadDetails.status || 'Unknown'}
                          color={statusColors[leadDetails.status] || 'default'}
                          size="small"
                        />
                        {leadDetails.sub_status && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {leadDetails.sub_status}
                          </Typography>
                        )}
                      </Box>
                      <Chip size="small" label={`Demos: ${leadDetails.total_demos || 0}`} variant="outlined" />
                      <Chip size="small" label={`Completed: ${leadDetails.completed_demos || 0}`} variant="outlined" />
                      {['admin', 'sales', 'sales_supervisor', 'country_manager'].includes(user?.role) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ChatIcon />}
                          onClick={() => {
                            onClose();
                            navigate(`/whatsapp?leadId=${leadDetails.id}`);
                          }}
                        >
                          WhatsApp
                        </Button>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={`Created: ${leadDetails.created_at ? formatDate(leadDetails.created_at) : 'Unknown'}`}
                          variant="outlined"
                        />
                        <IconButton
                          size="small"
                          onClick={handleOpenCreationDateDialog}
                          title="Update creation date (Admin only)"
                          sx={{ color: 'primary.main' }}
                          aria-label="Update creation date"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    {(leadDetails.is_duplicate || Number(leadDetails.family_unique_count) > 1) && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                        {leadDetails.is_duplicate && (
                          <Chip size="small" color="warning" label="Duplicate" />
                        )}
                        {Number(leadDetails.family_unique_count) > 1 && (
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={`Same parent (${leadDetails.family_unique_count} children)`}
                          />
                        )}
                      </Box>
                    )}
                    {(leadDetails.siblings || []).length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Same parent
                        </Typography>
                        {(leadDetails.siblings || []).filter((s) => !s.is_duplicate).map((s) => (
                          <Typography key={s.id} variant="body2">
                            {s.first_name || s.display_id} · {s.status || '—'}
                            {s.display_id ? ` · ${s.display_id}` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {(leadDetails.enrollment_submitted_at || leadDetails.converted_student_id) && (
                      <Alert severity="success" sx={{ mt: 2, mb: 0 }}>
                        Enrollment form submitted
                        {leadDetails.enrollment_submitted_at
                          ? ` on ${formatDate(leadDetails.enrollment_submitted_at)}`
                          : ''}
                        {leadDetails.converted_student_id && (
                          <>
                            {' · '}
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                onClose();
                                window.location.href = `/students?studentId=${leadDetails.converted_student_id}`;
                              }}
                            >
                              Open student
                              {leadDetails.converted_student_display_id
                                ? ` (${leadDetails.converted_student_display_id})`
                                : ''}
                            </Button>
                          </>
                        )}
                      </Alert>
                    )}

                    {leadDetails.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        <strong>Notes:</strong> {leadDetails.notes}
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <OverviewBand title="Contact">
                      <OverviewField label="Lead ID" value={leadDetails.display_id} />
                      <OverviewField
                        label="Student"
                        value={formatFullName(leadDetails.first_name, leadDetails.last_name)}
                      />
                      <OverviewField label="Gender" value={leadDetails.gender} />
                      <OverviewField
                        label="Primary contact"
                        value={formatParentContactLabel(leadDetails) || formatFullName(
                          leadDetails.parent_first_name,
                          leadDetails.parent_last_name
                        )}
                      />
                      <OverviewField label="Father" value={formatStoredPersonName(leadDetails.father_name)} />
                      <OverviewField label="Mother" value={formatStoredPersonName(leadDetails.mother_name)} />
                      <OverviewField label="Email" value={leadDetails.email} />
                      <OverviewField label="Phone" value={leadDetails.phone} />
                    </OverviewBand>

                    <Divider sx={{ my: 2 }} />

                    <OverviewBand title="Location">
                      <OverviewField label="Country" value={leadDetails.country} />
                      <OverviewField
                        label="State / City"
                        value={[leadDetails.state, leadDetails.city].filter(Boolean).join(', ')}
                      />
                      <OverviewField label="Timezone" value={leadDetails.timezone} />
                      <OverviewField label="Address" value={leadDetails.address} xs={12} sm={6} />
                    </OverviewBand>

                    <Divider sx={{ my: 2 }} />

                    <OverviewBand title="Source">
                      <OverviewField label="Source" value={leadDetails.source} />
                      <OverviewField label="Campaign" value={leadDetails.campaign_name} />
                      <OverviewField
                        label="Lead acquired"
                        value={leadDetails.lead_acquired_at ? formatCalendarDate(leadDetails.lead_acquired_at) : null}
                      />
                      <OverviewField
                        label="Assigned to"
                        value={
                          leadDetails.assigned_to
                            ? (leadDetails.assigned_admin_name || '').trim() || 'Assigned'
                            : 'Unassigned'
                        }
                      />
                    </OverviewBand>

                    <Divider sx={{ my: 2 }} />

                    <OverviewBand title="Academic">
                      <OverviewField label="Child age" value={formatChildAgeLabel(leadDetails)} />
                      <OverviewField
                        label="Date of birth"
                        value={leadDetails.date_of_birth ? formatCalendarDate(leadDetails.date_of_birth) : null}
                      />
                      <OverviewField label="Age group" value={leadDetails.age_group} />
                      <OverviewField label="Class / grade" value={leadDetails.class_grade} />
                      <OverviewField label="Student type" value={leadDetails.student_type} />
                      <OverviewField label="Class preference" value={leadDetails.class_type_preference} />
                      <OverviewField label="Learning level" value={leadDetails.learning_level} />
                      <OverviewField
                        label="Availability"
                        value={formatLeadTimePreferences(leadDetails.time_preferences)}
                      />
                    </OverviewBand>

                    <Divider sx={{ my: 2 }} />

                    <OverviewBand title="School">
                      <OverviewField label="School name" value={leadDetails.school_name} />
                      <OverviewField label="School location" value={leadDetails.school_location} />
                      <OverviewField label="School address" value={leadDetails.school_address} xs={12} sm={6} />
                    </OverviewBand>

                    <Divider sx={{ my: 2 }} />

                    <OverviewBand title="Additional">
                      <OverviewField label="Hobby" value={leadDetails.hobby} />
                      <OverviewField
                        label="Fees"
                        value={
                          leadDetails.fees != null && leadDetails.fees !== ''
                            ? String(leadDetails.fees)
                            : null
                        }
                      />
                      <OverviewField
                        label="Discount (%)"
                        value={
                          leadDetails.discount != null && leadDetails.discount !== ''
                            ? String(leadDetails.discount)
                            : null
                        }
                      />
                      <OverviewField label="Support requirements" value={leadDetails.support_requirements} />
                      <OverviewField label="Facebook IDs" value={formatListDisplay(leadDetails.parent_fb_ids)} />
                      <OverviewField label="LinkedIn IDs" value={formatListDisplay(leadDetails.parent_linkedin_ids)} />
                      <OverviewField label="Instagram IDs" value={formatListDisplay(leadDetails.parent_instagram_ids)} />
                      {(leadDetails.emergency_contact_name || leadDetails.emergency_contact_phone) && (
                        <OverviewField
                          label="Emergency contact"
                          value={
                            [leadDetails.emergency_contact_name, leadDetails.emergency_contact_phone]
                              .filter(Boolean)
                              .join(' · ')
                          }
                          sm={6}
                          xs={12}
                        />
                      )}
                    </OverviewBand>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {tabValue === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Demo History
                </Typography>
                {leadDetails.demos && leadDetails.demos.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date & Time</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Teacher</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leadDetails.demos.map((demo) => (
                          <TableRow key={demo.id}>
                            <TableCell>
                              <DemoCountryDateLines demo={demo} viewerTimezone={viewerTimezone} />
                              <Box sx={{ mt: 0.5 }}>
                                <DemoCountryTimeLines demo={demo} viewerTimezone={viewerTimezone} />
                              </Box>
                            </TableCell>
                            <TableCell>{demo.duration_minutes} min</TableCell>
                            <TableCell>{demo.teacher_name || 'Not assigned'}</TableCell>
                            <TableCell>
                              <Chip
                                label={demoStatusLabels[demo.status]}
                                color={demoStatusColors[demo.status]}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {demo.notes || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No demos scheduled yet.
                  </Typography>
                )}
              </Box>
            )}

            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Status History
                </Typography>
                {leadDetails.status_history && leadDetails.status_history.length > 0 ? (
                  <List>
                    {leadDetails.status_history.map((history, index) => (
                      <React.Fragment key={history.id}>
                        <ListItem>
                          <ListItemIcon>
                            <HistoryIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={statusLabels[history.new_status] || history.new_status}
                                    color={statusColors[history.new_status]}
                                    size="small"
                                  />
                                  {history.new_sub_status && (
                                    <Chip
                                      label={history.new_sub_status}
                                      variant="outlined"
                                      size="small"
                                      color="default"
                                    />
                                  )}
                                </Box>
                                {history.old_status && (
                                  <>
                                    <Typography variant="body2" color="text.secondary">from</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip
                                        label={statusLabels[history.old_status] || history.old_status}
                                        variant="outlined"
                                        size="small"
                                      />
                                      {history.old_sub_status && (
                                        <Chip
                                          label={history.old_sub_status}
                                          variant="outlined"
                                          size="small"
                                          color="default"
                                        />
                                      )}
                                    </Box>
                                  </>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {history.notes && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    {history.notes}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateTime(history.created_at)} by {history.changed_by_name}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < leadDetails.status_history.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No status changes recorded.
                  </Typography>
                )}
              </Box>
            )}

            {tabValue === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Activity Timeline
                </Typography>
                {combinedTimeline.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No activity recorded yet.
                  </Typography>
                )}
                {combinedTimeline.map((item) => (
                  <Paper key={`${item.kind}-${item.id}`} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Typography fontWeight={600}>
                      {item.kind === 'status'
                        ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography component="span" fontWeight={600}>
                                Status: {statusLabels[item.new_status] || item.new_status}
                              </Typography>
                              {item.new_sub_status && (
                                <Chip label={item.new_sub_status} variant="outlined" size="small" />
                              )}
                              {item.old_status && (
                                <>
                                  <Typography variant="body2" color="text.secondary" component="span">from</Typography>
                                  <Typography component="span">{statusLabels[item.old_status] || item.old_status}</Typography>
                                  {item.old_sub_status && (
                                    <Chip label={item.old_sub_status} variant="outlined" size="small" />
                                  )}
                                </>
                              )}
                            </Box>
                          )
                        : item.activity_description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(item.performed_by_name || item.changed_by_name || 'Unknown')} • {formatDateTimeInViewerTZ(item.ts, viewerTimezone)}
                    </Typography>
                    {item.kind === 'status' && item.notes && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {item.notes}
                      </Typography>
                    )}
                    {item.metadata && (
                      <Typography variant="caption" color="text.secondary">
                        {JSON.stringify(item.metadata)}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            {tabValue === 4 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add note"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          handleAddNote();
                        }
                      }}
                      multiline
                      rows={2}
                      placeholder="Type your note here... (Ctrl+Enter to submit)"
                    />
                    <Button variant="contained" onClick={handleAddNote} disabled={!noteText.trim()}>
                      Add
                    </Button>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showDeletedNotes}
                        onChange={(e) => setShowDeletedNotes(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show deleted"
                  />
                </Stack>

                {notes.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                      No notes yet. Add your first note above.
                  </Typography>
                  </Box>
                ) : (
                  <Box>
                    {(() => {
                      const grouped = groupNotesByDate(notes);
                      const NoteCard = ({ note }) => {
                        const [expanded, setExpanded] = useState(false);
                        const isLongNote = note.note_text && note.note_text.length > 200;
                        const displayText = isLongNote && !expanded 
                          ? note.note_text.substring(0, 200) + '...'
                          : note.note_text;

                        return (
                          <Paper 
                            key={note.id} 
                            variant="outlined" 
                            sx={{ 
                              p: 2, 
                              mb: 1.5,
                              backgroundColor: note.is_deleted ? 'action.disabledBackground' : 'background.paper',
                              opacity: note.is_deleted ? 0.6 : 1,
                              borderLeft: '3px solid',
                              borderLeftColor: note.is_deleted ? 'error.main' : 'primary.main'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                  <Typography fontWeight={600} variant="subtitle2">
                                    {note.created_by_name || 'Unknown'}
                                  </Typography>
                                  {note.status && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Chip
                                        label={statusLabels[note.status] || note.status}
                                        size="small"
                                        color={statusColors[note.status]}
                                        variant="outlined"
                                      />
                                      {note.sub_status && (
                                        <Chip
                                          label={note.sub_status}
                                          size="small"
                                          variant="outlined"
                                          color="default"
                                        />
                                      )}
                                    </Box>
                                  )}
                                  {note.is_deleted && (
                                    <Chip label="Deleted" size="small" color="error" variant="outlined" />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {formatRelativeTime(note.created_at)}
                                  {!note.is_deleted && (
                                    <span> • {formatDateInViewerTZ(note.created_at, viewerTimezone)}</span>
                                  )}
                                </Typography>
                              </Box>
                              {!note.is_deleted && (
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={() => handleDeleteNote(note.id)}
                                  sx={{ ml: 1 }}
                                >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: note.is_deleted ? 'text.secondary' : 'text.primary'
                              }}
                            >
                              {displayText}
                            </Typography>
                            {isLongNote && (
                              <Button
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                                sx={{ mt: 1, textTransform: 'none' }}
                              >
                                {expanded ? 'Show less' : 'Read more'}
                              </Button>
                            )}
                          </Paper>
                        );
                      };

                      return (
                        <>
                          {grouped.today.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                                Today
                              </Typography>
                              {grouped.today.map(note => <NoteCard key={note.id} note={note} />)}
                            </Box>
                          )}
                          {grouped.yesterday.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
                                Yesterday
                              </Typography>
                              {grouped.yesterday.map(note => <NoteCard key={note.id} note={note} />)}
                            </Box>
                          )}
                          {grouped.thisWeek.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
                                This Week
                      </Typography>
                              {grouped.thisWeek.map(note => <NoteCard key={note.id} note={note} />)}
                            </Box>
                          )}
                          {grouped.older.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
                                Older
                      </Typography>
                              {grouped.older.map(note => <NoteCard key={note.id} note={note} />)}
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

      {/* Separate Dialog for Creation Date Update - Can be removed without affecting other functionality */}
      <Dialog open={openCreationDateDialog} onClose={handleCloseCreationDateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Creation Date</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the creation date for this lead. This will change when the lead was originally created in the system.
          </DialogContentText>
          <TextField
            fullWidth
            label="Creation Date"
            type="date"
            value={creationDate}
            onChange={(e) => setCreationDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreationDateDialog} disabled={updatingCreationDate}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateCreationDate} 
            variant="contained" 
            disabled={updatingCreationDate || !creationDate}
          >
            {updatingCreationDate ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return content;
};

export default LeadDetails;
